import { buildCoreApiUrl } from './common'

const AUTH_COOKIE_MAX_AGE = 60 * 60 * 24 * 30

function cookieScope(): string[] {
    const host = window.location.hostname.toLowerCase()
    const useSharedDomain = host === 'contractfinderpro.com' || host.endsWith('.contractfinderpro.com')
    const parts = ['Path=/', 'SameSite=Lax']
    if (useSharedDomain) parts.push('Domain=.contractfinderpro.com')
    if (window.location.protocol === 'https:') parts.push('Secure')
    return parts
}

function readCookie(name: string): string | null {
    const match = document.cookie
        .split(';')
        .map(c => c.trim())
        .find(c => c.startsWith(`${name}=`))
    if (!match) return null
    return decodeURIComponent(match.slice(name.length + 1))
}

function writeCookie(name: string, value: string): void {
    const parts = [`${name}=${encodeURIComponent(value)}`, ...cookieScope(), `Max-Age=${AUTH_COOKIE_MAX_AGE}`]
    document.cookie = parts.join('; ')
}

export function logout(): void {
    const expired = [...cookieScope(), 'Max-Age=0'].join('; ')
    document.cookie = `cfp_access_token=; ${expired}`
    document.cookie = `cfp_refresh_token=; ${expired}`
    try {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
    } catch { /* ignore */ }
    window.location.href = '/'
}

// Bounce the user to /login unless they're already there. One place
// the redirect lives so REST 401s and any future flows can share it.
export function redirectToLogin(): void {
    if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login'
    }
}

// Swap the refresh token for a fresh access token via /api/token
// (grant_type=refresh_token). On success the new tokens are written
// to the cfp_access_token / cfp_refresh_token cookies + localStorage
// and the function resolves to true. Returns false if there's no
// refresh token to begin with or the server rejects it (the caller
// then redirects to /login). Concurrent callers share one inflight
// request so we don't fire multiple refreshes when several 401s land
// at once.
let _refreshInflight: Promise<boolean> | null = null
export function tryRefreshToken(): Promise<boolean> {
    if (_refreshInflight) return _refreshInflight
    const refreshToken = readCookie('cfp_refresh_token')
    if (!refreshToken) return Promise.resolve(false)
    _refreshInflight = (async () => {
        try {
            const body = new URLSearchParams({
                grant_type: 'refresh_token',
                refresh_token: refreshToken,
            })
            const res = await fetch(buildCoreApiUrl('api/token'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString(),
            })
            if (!res.ok) return false
            const data = await res.json()
            if (!data?.access_token) return false
            writeCookie('cfp_access_token', data.access_token)
            if (data.refresh_token) writeCookie('cfp_refresh_token', data.refresh_token)
            try {
                localStorage.setItem('access_token', data.access_token)
                if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token)
            } catch { /* ignore */ }
            return true
        } catch {
            return false
        } finally {
            // Release the inflight lock on the next tick so concurrent
            // callers that started during the request still piggyback on
            // the same result, while a subsequent fresh attempt is free
            // to fire if a later 401 lands.
            setTimeout(() => { _refreshInflight = null }, 0)
        }
    })()
    return _refreshInflight
}
